/**
 * BillingPage Component
 * SESSION_186: Frontend Billing Page
 * SESSION_205: Added polling for Pending status to catch webhook updates
 *
 * Customer page for viewing and managing subscription.
 *
 * Features:
 * - Display current subscription status
 * - Show subscription details (plan, amount, billing dates)
 * - Cancel subscription functionality
 * - Handle loading, error, and no-subscription states
 * - Auto-polling when status is Pending to catch webhook updates
 *
 * Route: /billing
 */

import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Stack,
  Title,
  Text,
  LoadingOverlay,
  Alert,
  Group,
  ThemeIcon
} from '@mantine/core';
import { IconCreditCard, IconAlertCircle } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import {
  useCancelSubscriptionMutation
} from '../../../application/queries/subscriptionQueries';
import { queryKeys } from '../../../application/queries/queryKeys';
import { CACHE_CONFIG } from '../../../application/queries/queryClient';
import { SubscriptionRepository } from '../../../infrastructure/repositories/SubscriptionRepository';
import { apiClient } from '../../../infrastructure/api/ApiClient';
import { GetMySubscriptionResponse } from '../../../application/dto/subscription';
import { SubscriptionStatus, NoSubscription } from '../../components/billing';

// Polling interval when subscription is Pending (5 seconds)
const PENDING_POLL_INTERVAL = 5000;

/**
 * BillingPage Component
 *
 * Displays subscription information and management controls.
 */
export const BillingPage: React.FC = (): React.ReactElement => {
  const subscriptionRepository = useMemo(() => new SubscriptionRepository(apiClient), []);

  // SESSION_205: Use custom query with conditional polling for Pending status
  const {
    data: subscriptionResponse,
    isLoading,
    error
  } = useQuery({
    queryKey: queryKeys.subscriptions.my(),
    queryFn: async (): Promise<GetMySubscriptionResponse> => {
      return subscriptionRepository.getMySubscription();
    },
    staleTime: CACHE_CONFIG.USER.staleTime,
    gcTime: CACHE_CONFIG.USER.gcTime,
    // Poll every 5 seconds if status is Pending, otherwise don't poll
    refetchInterval: (query): number | false => {
      const data = query.state.data as GetMySubscriptionResponse | undefined;
      const isPending = data?.subscription?.status === 'Pending';
      return isPending ? PENDING_POLL_INTERVAL : false;
    }
  });

  // Cancel subscription mutation
  const cancelMutation = useCancelSubscriptionMutation();

  const handleCancelSubscription = useCallback((): void => {
    if (subscriptionResponse?.subscription?.id) {
      cancelMutation.mutate({
        subscriptionId: subscriptionResponse.subscription.id,
        immediate: false
      });
    }
  }, [subscriptionResponse?.subscription?.id, cancelMutation]);

  // Loading state
  if (isLoading) {
    return (
      <Box py="xl">
        <LoadingOverlay visible />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Subscription"
          color="red"
        >
          {error.message || 'Could not load your subscription information. Please try again.'}
        </Alert>
      </Box>
    );
  }

  const hasSubscription = subscriptionResponse?.hasSubscription ?? false;
  const subscription = subscriptionResponse?.subscription ?? null;

  return (
    <Box py="xl">
      <Stack gap="lg">
        {/* Page Header */}
        <Group gap="md">
          <ThemeIcon size="xl" radius="md" variant="light" color="blue">
            <IconCreditCard size={28} />
          </ThemeIcon>
          <Stack gap={4}>
            <Title order={2}>Billing</Title>
            <Text size="sm" c="dimmed">
              Manage your subscription and billing information
            </Text>
          </Stack>
        </Group>

        {/* Subscription Content */}
        {hasSubscription && subscription ? (
          <SubscriptionStatus
            subscription={subscription}
            onCancel={handleCancelSubscription}
            isCanceling={cancelMutation.isPending}
          />
        ) : (
          <NoSubscription />
        )}
      </Stack>
    </Box>
  );
};
